import React, { useContext, useEffect, useMemo, useState } from 'react';
import { LangContext } from '../contexts/langContext';

const STORAGE_KEY = 'inapp_gate_dismissed';
const APPS = ['instagram', 'facebook', 'telegram', 'whatsapp'];

const fill = (str, vars = {}) =>
  Object.keys(vars).reduce(
    (acc, key) => acc.split(`{${key}}`).join(vars[key]),
    str || ''
  );

export function detectInAppBrowser() {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const preview = (params.get('inapp') || '').toLowerCase();
  if (preview === 'off') return null;
  if (APPS.includes(preview)) {
    return {
      app: preview,
      platform: detectPlatform(navigator.userAgent || ''),
      preview: true,
    };
  }

  const ua = navigator.userAgent || '';
  const platform = detectPlatform(ua);

  if (/instagram/i.test(ua)) {
    return { app: 'instagram', platform };
  }

  if (/fban|fbav|fb_iab|fbios|fb4a|messenger/i.test(ua)) {
    return { app: 'facebook', platform };
  }

  const telegramMiniApp = Boolean(window.Telegram?.WebApp?.initData);
  const telegramWebview = Boolean(
    window.TelegramWebviewProxy
    || window.TelegramWebview
    || window.webkit?.messageHandlers?.TelegramWebviewProxy
  );
  if (!telegramMiniApp && (/telegram/i.test(ua) || telegramWebview)) {
    return { app: 'telegram', platform };
  }

  // Android WebView and WhatsApp Business IAB put WhatsApp/… in the UA.
  // iOS often opens Safari instead — then there is no WhatsApp token, and we skip.
  if (/whatsapp/i.test(ua)) {
    return { app: 'whatsapp', platform };
  }

  return null;
}

function detectPlatform(ua) {
  if (/android/i.test(ua)) return 'android';
  return 'ios';
}

function copyToClipboard(text) {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    return navigator.clipboard.writeText(text).then(() => true).catch(() => copyFallback(text));
  }
  return Promise.resolve(copyFallback(text));
}

function copyFallback(text) {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}

function MenuIllustration({ hostname, openLabel }) {
  const host = hostname.length > 28 ? `${hostname.slice(0, 26)}…` : hostname;
  return (
    <svg
      className="x_inapp_illu"
      viewBox="0 0 320 176"
      role="img"
      aria-hidden="true"
    >
      <rect x="0" y="0" width="320" height="176" rx="18" fill="#1c1c1e" />
      <text x="16" y="28" fill="#8e8e93" fontSize="14">✕</text>
      <text x="160" y="29" textAnchor="middle" fill="#aeaeb2" fontSize="12" fontFamily="system-ui, sans-serif">
        {host}
      </text>
      <circle cx="286" cy="24" r="1.7" fill="#d1d1d6" />
      <circle cx="296" cy="24" r="1.7" fill="#d1d1d6" />
      <circle cx="306" cy="24" r="1.7" fill="#d1d1d6" />
      <rect x="12" y="44" width="296" height="120" rx="14" fill="#2c2c2e" />
      <text x="28" y="70" fill="#f2f2f7" fontSize="13" fontFamily="system-ui, sans-serif">↗     Link history</text>
      <text x="28" y="96" fill="#f2f2f7" fontSize="13" fontFamily="system-ui, sans-serif">✈     Send</text>
      <rect x="16" y="108" width="288" height="26" rx="8" fill="#3a3a3c" />
      <text x="28" y="126" fill="#ffffff" fontSize="13" fontFamily="system-ui, sans-serif" fontWeight="600">
        ↗     {openLabel}
      </text>
      <text x="28" y="152" fill="#f2f2f7" fontSize="13" fontFamily="system-ui, sans-serif">☰     Browser settings</text>
      <path d="M306 28 C 312 56 312 96 292 121" fill="none" stroke="#b7a8ff" strokeWidth="2.4" strokeLinecap="round" />
      <polygon points="292,121 284,116 288,126" fill="#b7a8ff" />
    </svg>
  );
}

export function InAppBrowserGate() {
  const { $ui } = useContext(LangContext);
  const detected = useMemo(() => detectInAppBrowser(), []);
  const [open, setOpen] = useState(() => {
    if (!detected) return false;
    if (detected.preview) return true;
    try {
      return sessionStorage.getItem(STORAGE_KEY) !== '1';
    } catch (e) {
      return true;
    }
  });
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add('noscroll');
    return () => document.body.classList.remove('noscroll');
  }, [open]);

  if (!detected || !open) return null;

  const appName = $ui(`inapp_app_${detected.app}`);
  const browserName = detected.platform === 'android' ? 'Chrome' : 'Safari';
  const vars = { app: appName, browser: browserName };
  const hintKey = `inapp_hint_${detected.app}_${detected.platform}`;
  const hintFallback = `inapp_hint_${detected.app}`;
  const hint = $ui(hintKey) !== hintKey
    ? $ui(hintKey)
    : ($ui(hintFallback) !== hintFallback ? $ui(hintFallback) : $ui('inapp_hint'));
  const openLabel = detected.app === 'instagram'
    ? $ui('inapp_menu_instagram')
    : detected.platform === 'android'
      ? $ui('inapp_menu_chrome')
      : $ui('inapp_menu_safari');
  const href = window.location.href;
  const hostname = window.location.hostname.replace(/^www\./, '');

  const dismiss = () => {
    if (!detected.preview) {
      try {
        sessionStorage.setItem(STORAGE_KEY, '1');
      } catch (e) { /* ignore */ }
    }
    setOpen(false);
  };

  const handleCopy = () => {
    copyToClipboard(href).then((ok) => {
      if (ok) {
        setCopied(true);
        setCopyFailed(false);
        window.setTimeout(() => setCopied(false), 2000);
      } else {
        setCopyFailed(true);
      }
    });
  };

  return (
    <div className="x_inapp" role="dialog" aria-modal="true" aria-labelledby="x_inapp_title">
      <div className="x_inapp_z" onClick={dismiss} />
      <div className="x_inapp_card">
        <div className="x_inapp_banner">
          <span>{fill($ui('inapp_banner'), vars)}</span>
          <button type="button" className="x_inapp_close" onClick={dismiss} aria-label={$ui('inapp_close')}>
            <span className="t_icon t_icon--20">close</span>
          </button>
        </div>

        <h2 id="x_inapp_title" className="x_inapp_title">
          {fill($ui('inapp_title'), vars)}
        </h2>
        <p className="x_inapp_body">
          {fill($ui('inapp_body'), vars)}
        </p>
        <p className="x_inapp_hint">
          {hint}
        </p>

        <MenuIllustration hostname={hostname} openLabel={openLabel} />

        <button type="button" className="x_inapp_copy" onClick={handleCopy}>
          <span className="t_icon t_icon--20">content_copy</span>
          <span>{copied ? $ui('inapp_copied') : $ui('inapp_copy')}</span>
        </button>
        {copyFailed && (
          <div className="x_inapp_url" onClick={(e) => e.stopPropagation()}>
            {href}
          </div>
        )}

        <button type="button" className="x_inapp_continue" onClick={dismiss}>
          {$ui('inapp_continue')}
        </button>
      </div>
    </div>
  );
}
